import { Component } from '@theme/component';

/**
 * @typedef {Object} TextExpandRefs
 * @property {HTMLElement} textExpandCopy - The clamped copy wrapper
 * @property {HTMLElement} textExpandButton - The toggle
 */

/**
 * @extends {Component<TextExpandRefs>}
 */
class TextExpandComponent extends Component {
  requiredRefs = ['textExpandCopy', 'textExpandButton'];

  /** @constant {string} */
  static ACTIVE = '_active';

  /** @constant {string} */
  static EXPANDED = '_expanded';

  /** @type {Animation | undefined} */
  #animation;

  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  /** @constant {number} */
  #animationSpeed = 300;

  connectedCallback() {
    super.connectedCallback();

    this.#syncAffordance();

    this.#resizeObserver = new ResizeObserver(() => this.#syncAffordance());
    this.#resizeObserver.observe(this.refs.textExpandCopy);
    this.refs.textExpandCopy.addEventListener('focusin', this.#handleFocusIn);
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.#resizeObserver?.disconnect();
    this.refs.textExpandCopy?.removeEventListener('focusin', this.#handleFocusIn);
    this.#animation?.cancel();
  }

  get #expanded() {
    return this.classList.contains(TextExpandComponent.EXPANDED);
  }

  /**
   * @type {HTMLElement | null}
   */
  get #clampTarget() {
    const child = this.refs.textExpandCopy?.firstElementChild;
    return child instanceof HTMLElement ? child : null;
  }

  #syncAffordance = () => {
    if (this.#expanded) return;

    const target = this.#clampTarget;
    if (!target) return;

    this.classList.add(TextExpandComponent.ACTIVE);
    const overflows = target.scrollHeight - target.clientHeight > 1;
    this.classList.toggle(TextExpandComponent.ACTIVE, overflows);
  };

  /**
   * @param {Event} event
   */
  #handleFocusIn = (event) => {
    if (this.#expanded || !this.classList.contains(TextExpandComponent.ACTIVE)) return;
    if (!(event.target instanceof HTMLElement)) return;

    this.refs.textExpandButton.click();
  };

  /**
   * @param {Event} event
   */
  toggle = (event) => {
    event.preventDefault();

    const wrapper = this.refs.textExpandCopy;
    if (!wrapper || !this.#clampTarget) return;

    const startHeight = wrapper.offsetHeight;
    const expanding = !this.#expanded;

    this.classList.toggle(TextExpandComponent.EXPANDED, expanding);
    this.refs.textExpandButton.setAttribute('aria-expanded', String(expanding));

    const endHeight = wrapper.offsetHeight;
    if (startHeight === endHeight) return;

    this.#animation?.cancel();
    wrapper.style.overflow = 'hidden';
    this.#animation = wrapper.animate(
      { height: [`${startHeight}px`, `${endHeight}px`] },
      { duration: this.#animationSpeed, easing: 'ease-in-out' }
    );
    this.#animation.onfinish = () => {
      wrapper.style.removeProperty('overflow');
      if (!this.#expanded) {
        const target = this.#clampTarget;
        if (target) target.scrollTop = 0;
        this.#syncAffordance();
      }
    };
  };
}

if (!customElements.get('text-expand-component')) {
  customElements.define('text-expand-component', TextExpandComponent);
}
